import { useState } from 'react'
import { useAppDispatch } from '@/utils/typeHelpers'
import { Truncate } from '@re-dev/react-truncate'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'
import { useReselector } from '@/utils/sharedHooks'
import { TransactionOrSplit } from '@/reducers/admin/allTransactions.slice'

import { Text, Button, Link, Separator, Alert, Tooltip } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { NotesInput } from './NotesInput'
import { DetailsTooltip } from './DetailsTooltip'
import { selectReceiptsForTransaction } from '@/features/user/your-practice/documents/lib/userDocuments.selector'
import { destroySingleUserDocument } from '@/features/user/your-practice/documents/lib/userDocuments.slice'

interface NotesPanelProps {
  transaction: TransactionOrSplit
  toggleFileModal: () => void
  splitIndex?: number
  parentId?: number
  setPreventClose: (value: boolean) => void
}

export const NotesPanel = ({
  transaction,
  toggleFileModal,
  splitIndex,
  parentId,
  setPreventClose,
}: NotesPanelProps) => {
  const [error, setError] = useState('')
  const transactionIdForDocs = parentId || transaction.id

  const documents = useReselector(
    selectReceiptsForTransaction,
    transactionIdForDocs,
    splitIndex
  )
  const dispatch = useAppDispatch()

  const deleteFile = async (fileId: number) => {
    setError('')
    const deleted = await destroySingleUserDocument(fileId)(dispatch)
    if (!deleted) setError('Error deleting your receipt.')
  }
  return (
    <div className="flex flex-col gap-4">
      <div className="flex-1 grow mt-2">
        <NotesInput transaction={transaction} />
      </div>
      <Separator />
      <div className="flex-1 grow">
        <div className="flex flex-row items-center">
          <Text variant={TypographyStyles.LABEL_MD} color={TextColors.MUTED}>
            Receipt
          </Text>
          <DetailsTooltip label="Receipt">
            <Text>Uploading a receipt is optional but recommended.</Text>
          </DetailsTooltip>
        </div>

        {documents?.map((doc) => (
          <div className="mb-2" key={doc.id}>
            <Tooltip>
              <Tooltip.Trigger asChild>
                <Link href={doc?.signedUrl || ''} newPage>
                  <Truncate width={350}>{doc?.fileDescription}</Truncate>
                </Link>
              </Tooltip.Trigger>
              <Tooltip.Content>
                <Text variant={TypographyStyles.BODY_SM}>
                  {doc?.fileDescription}
                </Text>
              </Tooltip.Content>
            </Tooltip>
            <Button
              variant="discreet"
              onClick={() => deleteFile(doc.id)}
              iconLeft={regular('trash-can')}
              size="square"
              className="ml-2"
            />
            <br />
          </div>
        ))}

        <Button
          variant="secondary"
          iconLeft={regular('upload')}
          onClick={() => {
            toggleFileModal()
            setPreventClose(true)
          }}
        >
          Upload
        </Button>
      </div>
      {error && (
        <Alert variant="error" className="w-full mt-4" description={error} />
      )}
    </div>
  )
}
